import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Paper, Typography, IconButton, Tooltip, CircularProgress, Chip,
} from '@mui/material';
import {
    Call, CallEnd, CallMade, Mic, MicOff, PhoneInTalk, Close,
} from '@mui/icons-material';
import axios from 'axios';
import { global_link } from './Globallink';

const api = axios.create({ baseURL: global_link });

/**
 * Browser WebRTC softphone for WhatsApp Cloud API Calling.
 *
 * Inbound (USER_INITIATED):
 *   connect webhook with SDP offer → ring UI → Accept → pre_accept + accept with SDP answer
 *
 * Outbound (BUSINESS_INITIATED):
 *   startOutbound() → create offer → POST connect → wait for connect webhook SDP answer
 *
 * Multi-agent behaviour:
 *   - Free agents receive the ring and can answer.
 *   - Busy agents silently ignore the incoming call (no reject is sent to Meta).
 *   - This allows other free agents to still pick up the same call.
 *
 * Props:
 *   getHeaders()          → auth headers
 *   socket                → org socket.io client (listens WHATSAPP_CALL_EVENT)
 *   activeContactNumber   → E.164-ish number for outbound (optional)
 *   onNotify({severity, message})
 */
export default function WhatsAppWebRTCSoftphone({
    getHeaders,
    socket,
    activeContactNumber,
    onNotify,
}) {
    const [incoming, setIncoming] = useState(null); // { call_id, from, sdp, contact_name }
    const [activeCall, setActiveCall] = useState(null); // { call_id, peer, direction, contact }
    const [status, setStatus] = useState('idle'); // idle | ringing | connecting | in_call
    const [muted, setMuted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [elapsed, setElapsed] = useState(0); // seconds in call
    const callStartedAtRef = useRef(null);

    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const pendingOutboundRef = useRef(null); // { contact, resolve, reject }
    const ringtoneRef = useRef(null);

    // Keep latest status / activeCall in refs so the socket handler always sees current values
    const statusRef = useRef(status);
    const activeCallRef = useRef(activeCall);
    useEffect(() => { statusRef.current = status; }, [status]);
    useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

    const notify = useCallback((severity, message) => {
        if (typeof onNotify === 'function') onNotify({ severity, message });
    }, [onNotify]);

    const cleanupMedia = useCallback(() => {
        try {
            if (pcRef.current) {
                pcRef.current.onicecandidate = null;
                pcRef.current.ontrack = null;
                pcRef.current.close();
            }
        } catch { /* ignore */ }
        pcRef.current = null;
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((t) => t.stop());
            localStreamRef.current = null;
        }
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        setMuted(false);
    }, []);

    const createPeerConnection = useCallback(async () => {
        // Meta relays ICE; empty iceServers is often enough. Add TURN later if needed.
        const pc = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        });

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
            video: false,
        });
        localStreamRef.current = stream;
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        pc.ontrack = (ev) => {
            const [remote] = ev.streams;
            if (remoteAudioRef.current && remote) {
                remoteAudioRef.current.srcObject = remote;
                remoteAudioRef.current.play().catch(() => {});
            }
        };

        pcRef.current = pc;
        return pc;
    }, []);

    const postAction = useCallback(async (body) => {
        const res = await api.post('/whatsapp/calling/action', body, { headers: getHeaders() });
        return res.data;
    }, [getHeaders]);

    // ── Socket: inbound ring + outbound answer + terminate ─────────────────
    useEffect(() => {
        if (!socket) return undefined;

        const onCallEvent = async (eventData) => {
            if (!eventData || eventData.type !== 'call_event') return;
            const d = eventData.data || {};
            const event = String(d.event || '').toLowerCase();
            const direction = String(d.direction || '').toUpperCase();

            if (event === 'terminate') {
                if (
                    activeCallRef.current?.call_id === d.call_id ||
                    incoming?.call_id === d.call_id
                ) {
                    cleanupMedia();
                    setIncoming(null);
                    setActiveCall(null);
                    setStatus('idle');
                    notify('info', 'WhatsApp call ended');
                }
                return;
            }

            if (event !== 'connect') return;

            // Inbound: user called us — SDP offer
            if (direction.includes('USER') && d.session?.sdp) {
                const currentStatus = statusRef.current;

                // Already busy → silently ignore (do NOT reject).
                // This allows other free agents to still receive and answer the same call.
                if (
                    currentStatus === 'in_call' ||
                    currentStatus === 'connecting' ||
                    currentStatus === 'ringing' ||
                    activeCallRef.current
                ) {
                    console.log('Ignoring incoming WhatsApp call while busy:', d.call_id);
                    return;
                }

                // Free → show ring UI
                setIncoming({
                    call_id: d.call_id,
                    from: d.from,
                    contact_name: d.contact_name,
                    sdp: d.session.sdp,
                    sdp_type: d.session.sdp_type || 'offer',
                });
                setStatus('ringing');
                notify('info', `Incoming WhatsApp call from ${d.contact_name || d.from || 'customer'}`);
                return;
            }

            // Outbound: Meta returned SDP answer for our connect
            if (direction.includes('BUSINESS') && d.session?.sdp && pendingOutboundRef.current) {
                try {
                    const pc = pcRef.current;
                    if (!pc) return;
                    await pc.setRemoteDescription({
                        type: d.session.sdp_type || 'answer',
                        sdp: d.session.sdp,
                    });
                    setActiveCall({
                        call_id: d.call_id,
                        direction: 'outbound',
                        contact: pendingOutboundRef.current.contact,
                    });
                    setStatus('in_call');
                    pendingOutboundRef.current = null;
                    notify('success', 'WhatsApp call connected');
                } catch (err) {
                    console.error(err);
                    notify('error', err.message || 'Failed to apply remote SDP');
                    cleanupMedia();
                    setStatus('idle');
                    pendingOutboundRef.current = null;
                }
            }
        };

        socket.on('WHATSAPP_CALL_EVENT', onCallEvent);
        return () => socket.off('WHATSAPP_CALL_EVENT', onCallEvent);
    }, [socket, incoming, cleanupMedia, notify, postAction]);

    // Call duration timer (both inbound & outbound once connected)
    useEffect(() => {
        if (status !== 'in_call') {
            if (status === 'idle') {
                setElapsed(0);
                callStartedAtRef.current = null;
            }
            return undefined;
        }
        if (!callStartedAtRef.current) {
            callStartedAtRef.current = Date.now();
        }
        const id = setInterval(() => {
            const start = callStartedAtRef.current || Date.now();
            setElapsed(Math.floor((Date.now() - start) / 1000));
        }, 1000);
        return () => clearInterval(id);
    }, [status]);

    const formatElapsed = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    // Incoming call ringtone (loop until accept / reject / idle)
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (!ringtoneRef.current) {
            const audio = new Audio('/Tones/whatsapp_call.mp3');
            audio.loop = true;
            audio.preload = 'auto';
            ringtoneRef.current = audio;
        }
        const audio = ringtoneRef.current;

        if (status === 'ringing') {
            audio.currentTime = 0;
            audio.play().catch((err) => {
                console.warn('WhatsApp ringtone blocked until user gesture:', err?.message || err);
            });
        } else {
            try {
                audio.pause();
                audio.currentTime = 0;
            } catch { /* ignore */ }
        }

        return () => {
            try {
                audio.pause();
                audio.currentTime = 0;
            } catch { /* ignore */ }
        };
    }, [status]);

    // ── Accept inbound ─────────────────────────────────────────────────────
    const acceptIncoming = async () => {
        if (!incoming || busy) return;
        setBusy(true);
        setStatus('connecting');
        try {
            const pc = await createPeerConnection();
            await pc.setRemoteDescription({
                type: incoming.sdp_type || 'offer',
                sdp: incoming.sdp,
            });
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            // Wait briefly for ICE candidates to gather into localDescription
            await new Promise((r) => setTimeout(r, 500));
            const sdp = pc.localDescription?.sdp || answer.sdp;

            // Recommended: pre_accept then accept with the same SDP answer
            await postAction({
                action: 'pre_accept',
                call_id: incoming.call_id,
                sdp,
                sdp_type: 'answer',
            });
            await postAction({
                action: 'accept',
                call_id: incoming.call_id,
                sdp,
                sdp_type: 'answer',
            });

            setActiveCall({
                call_id: incoming.call_id,
                direction: 'inbound',
                contact: incoming.from,
            });
            setIncoming(null);
            setStatus('in_call');
            notify('success', 'WhatsApp call answered');
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.details?.error?.message
                || err?.response?.data?.error
                || err.message
                || 'Failed to answer call';
            notify('error', typeof msg === 'string' ? msg : JSON.stringify(msg));
            cleanupMedia();
            setStatus('idle');
            setIncoming(null);
        } finally {
            setBusy(false);
            try { window.dispatchEvent(new CustomEvent('waCallUiCleared')); } catch { /* ignore */ }
        }
    };

    const rejectIncoming = async () => {
        if (!incoming) return;
        setBusy(true);
        try {
            await postAction({ action: 'reject', call_id: incoming.call_id });
        } catch (err) {
            console.error(err);
        } finally {
            setIncoming(null);
            setStatus('idle');
            setBusy(false);
            try { window.dispatchEvent(new CustomEvent('waCallUiCleared')); } catch { /* ignore */ }
            notify('info', 'WhatsApp call rejected');
        }
    };

    // ── Outbound ───────────────────────────────────────────────────────────
    const startOutbound = async (contactNumber) => {
        const to = contactNumber || activeContactNumber;
        if (!to || busy || status === 'in_call' || status === 'ringing') return;
        setBusy(true);
        setStatus('connecting');
        try {
            const pc = await createPeerConnection();
            const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
            await pc.setLocalDescription(offer);
            await new Promise((r) => setTimeout(r, 500));
            const sdp = pc.localDescription?.sdp || offer.sdp;

            pendingOutboundRef.current = { contact: to };
            await postAction({
                action: 'connect',
                contact_number: to,
                sdp,
                sdp_type: 'offer',
            });
            // Wait for BUSINESS_INITIATED connect webhook with answer (handled in socket effect)
            notify('info', 'Calling on WhatsApp…');
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.details?.error?.message
                || err?.response?.data?.error
                || err.message
                || 'Failed to start WhatsApp call';
            notify('error', typeof msg === 'string' ? msg : JSON.stringify(msg));
            cleanupMedia();
            setStatus('idle');
            pendingOutboundRef.current = null;
        } finally {
            setBusy(false);
        }
    };

    const hangup = async () => {
        const callId = activeCall?.call_id || incoming?.call_id;
        setBusy(true);
        try {
            if (callId) {
                await postAction({ action: 'terminate', call_id: callId });
            }
        } catch (err) {
            console.error(err);
        } finally {
            cleanupMedia();
            setActiveCall(null);
            setIncoming(null);
            setStatus('idle');
            setBusy(false);
            try { window.dispatchEvent(new CustomEvent('waCallUiCleared')); } catch { /* ignore */ }
            notify('info', 'Call ended');
        }
    };

    const toggleMute = () => {
        const stream = localStreamRef.current;
        if (!stream) return;
        stream.getAudioTracks().forEach((t) => {
            t.enabled = !t.enabled;
        });
        setMuted((m) => !m);
    };

    // Expose for Sidebar / header (outbound + inbound answer/reject)
    useEffect(() => {
        window.__waStartOutboundCall = startOutbound;
        window.__waAcceptIncomingCall = acceptIncoming;
        window.__waRejectIncomingCall = rejectIncoming;
        return () => {
            if (window.__waStartOutboundCall === startOutbound) delete window.__waStartOutboundCall;
            if (window.__waAcceptIncomingCall === acceptIncoming) delete window.__waAcceptIncomingCall;
            if (window.__waRejectIncomingCall === rejectIncoming) delete window.__waRejectIncomingCall;
        };
    });

    if (status === 'idle' && !incoming) {
        return (
            <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
        );
    }

    return (
        <>
            <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
            <Paper
                elevation={6}
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1400,
                    p: 2,
                    minWidth: 280,
                    maxWidth: 340,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: status === 'ringing' ? 'success.main' : 'divider',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        WhatsApp Call
                    </Typography>
                    <Chip
                        size="small"
                        label={status.replace('_', ' ')}
                        color={status === 'in_call' ? 'success' : status === 'ringing' ? 'warning' : 'default'}
                    />
                </Box>

                {status === 'ringing' && incoming && (
                    <>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Incoming from{' '}
                            <strong>{incoming.contact_name || incoming.from || 'customer'}</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Call />}
                                disabled={busy}
                                onClick={acceptIncoming}
                            >
                                Accept
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                startIcon={<CallEnd />}
                                disabled={busy}
                                onClick={rejectIncoming}
                            >
                                Reject
                            </Button>
                        </Box>
                    </>
                )}

                {(status === 'connecting' || status === 'in_call') && (
                    <>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {status === 'connecting' ? 'Connecting…' : 'In call with'}{' '}
                            <strong>
                                {activeCall?.contact || incoming?.from || activeContactNumber || '—'}
                            </strong>
                        </Typography>
                        {status === 'in_call' && (
                            <Typography
                                variant="h6"
                                sx={{ mb: 1.5, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1, textAlign: 'center' }}
                            >
                                {formatElapsed(elapsed)}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {status === 'in_call' && (
                                <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                                    <IconButton onClick={toggleMute} color={muted ? 'warning' : 'default'}>
                                        {muted ? <MicOff /> : <Mic />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<CallEnd />}
                                disabled={busy}
                                onClick={hangup}
                            >
                                Hang up
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </>
    );
}

/** Helper for header buttons: start outbound WebRTC call if softphone is mounted */
export function startWhatsAppOutboundCall(contactNumber) {
    if (typeof window.__waStartOutboundCall === 'function') {
        return window.__waStartOutboundCall(contactNumber);
    }
    console.warn('WhatsAppWebRTCSoftphone not mounted');
    return undefined;
}